import { type Country } from "@/domain/country";
import { getHttpClientConfig } from "@/config/http-client";

import { isClientTokenError } from "./is-client-token-error";

export interface LoadBraintreePayPalInput {
	endpoint: string;
	country: Country;
	currency: "USD" | "CAD";
}

interface ClientTokenResponse {
	clientToken: string;
}

interface LoadPayPalSdkOptions {
	components: "messages";
	currency: string;
	vault: boolean;
	"enable-funding": "paylater";
}

async function fetchClientToken(
	endpoint: string,
	country: Country,
	forceRefresh: boolean
): Promise<string> {
	const params = new URLSearchParams({ country });
	if (forceRefresh) {
		params.set("forceRefresh", "true");
	}

	const response = await fetch(
		`${endpoint}/generateClientToken?${params.toString()}`,
		{
			signal: AbortSignal.timeout(
				getHttpClientConfig().timeoutSeconds * 1000
			)
		}
	);
	if (!response.ok) {
		throw new Error(
			`Failed to fetch PayPal client token (HTTP ${response.status}).`
		);
	}

	const data = (await response.json()) as ClientTokenResponse;
	if (!data?.clientToken) {
		throw new Error("Failed to fetch PayPal client token (no token).");
	}

	return data.clientToken;
}

async function loadMessagesSdk(
	input: LoadBraintreePayPalInput,
	forceRefresh: boolean
): Promise<void> {
	const clientToken = await fetchClientToken(
		input.endpoint,
		input.country,
		forceRefresh
	);

	const braintree = await import("braintree-web");

	const clientInstance = await braintree.client.create({
		authorization: clientToken
	});
	const paypalCheckoutInstance = await braintree.paypalCheckout.create({
		client: clientInstance
	});

	await paypalCheckoutInstance.loadPayPalSDK({
		components: "messages",
		currency: input.currency,
		vault: true,
		"enable-funding": "paylater"
	} as LoadPayPalSdkOptions as Parameters<
		typeof paypalCheckoutInstance.loadPayPalSDK
	>[0]);
}

export async function loadBraintreePayPalMessages(
	input: LoadBraintreePayPalInput
): Promise<void> {
	try {
		await loadMessagesSdk(input, false);
	} catch (err) {
		if (!isClientTokenError(err)) {
			throw err;
		}
		// The endpoint is cache-first, so a rejected token is retried once uncached.
		await loadMessagesSdk(input, true);
	}
}
